import { ArrowRight, Link2, QrCode, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden pt-20 pb-32">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-lime-200 dark:bg-lime-900 rounded-full opacity-40 dark:opacity-20 blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-lavender-200 dark:bg-lavender-900 rounded-full opacity-40 dark:opacity-20 blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-lime-100 dark:bg-lime-900 rounded-full opacity-30 dark:opacity-10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-lime-100 dark:bg-lime-900/30 rounded-full mb-8">
            <span className="text-sm font-medium text-lime-700 dark:text-lime-400">
              {t('landing.smartLinksForCreators')}
            </span>
            <ArrowRight className="w-4 h-4 text-lime-700 dark:text-lime-400" />
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-6xl font-bold text-dark-800 dark:text-white mb-6 leading-tight">
            {t('landing.heroTitle1')}
            <br />
            {t('landing.heroTitle3')}{' '}
            <span className="relative whitespace-nowrap">
              <span className="relative z-10">{t('landing.heroTitle2')}</span>
              <span className="absolute bottom-1 left-0 right-0 h-5 sm:h-6 bg-lime-300 dark:bg-lime-600 -rotate-1 -z-0" />
            </span>.
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-2xl mx-auto">
            {t('landing.heroSubtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <a href="#waitlist">
              <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                {t('waitlist.joinButton')}
              </Button>
            </a>
            <a href="#features">
              <Button variant="ghost" size="lg">
                {t('landing.seeHowItWorks')}
              </Button>
            </a>
          </div>

          {/* Trust Badge */}
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-16">
            <span>🛡️</span>
            <span>{t('landing.heroTrustBadge')}</span>
          </div>

          {/* Preview Cards */}
          <div className="relative">
            {/* Main card */}
            <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-elevated dark:shadow-none dark:border dark:border-dark-700 p-6 max-w-lg mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-lime-400 to-lavender-400 rounded-2xl" />
                <div className="text-left">
                  <h3 className="font-semibold text-dark-800 dark:text-white">klipr.it/@yourname</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t('landing.yourPersonalLinkHub')}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-dark-700 rounded-2xl">
                  <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                  </div>
                  <span className="text-dark-800 dark:text-white font-medium">{t('landing.exampleSpotify')}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-dark-700 rounded-2xl">
                  <div className="w-10 h-10 bg-dark-800 dark:bg-white rounded-xl flex items-center justify-center text-white dark:text-dark-800">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  </div>
                  <span className="text-dark-800 dark:text-white font-medium">{t('landing.exampleTikTok')}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-dark-700 rounded-2xl">
                  <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center text-white">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21.805 10.041H21.2V10H12v4h5.651C16.827 16.784 14.673 18.5 12 18.5 8.686 18.5 6 15.814 6 12.5S8.686 6.5 12 6.5c1.558 0 2.973.596 4.035 1.569L18.157 5.85C16.437 4.252 14.335 3.4 12 3.4c-5.024 0-9.1 4.077-9.1 9.1 0 5.024 4.077 9.1 9.1 9.1 5.024 0 9.1-4.077 9.1-9.1 0-.61-.062-1.206-.295-1.459z"/>
                    </svg>
                  </div>
                  <span className="text-dark-800 dark:text-white font-medium">{t('landing.exampleAmazon')}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -left-4 top-1/4 bg-lime-400 rounded-2xl p-4 shadow-lg hidden lg:flex items-center gap-3 animate-float">
              <Link2 className="w-6 h-6 text-dark-800" />
              <span className="font-semibold text-dark-800">{t('landing.smartDeeplinks')}</span>
            </div>

            <div className="absolute -right-4 top-1/3 bg-lavender-400 rounded-2xl p-4 shadow-lg hidden lg:flex items-center gap-3 animate-float" style={{ animationDelay: '1s' }}>
              <QrCode className="w-6 h-6 text-dark-800" />
              <span className="font-semibold text-dark-800">{t('landing.customQR')}</span>
            </div>

            <div className="absolute -right-8 bottom-10 bg-dark-800 rounded-2xl p-4 shadow-lg hidden lg:flex items-center gap-3 animate-float" style={{ animationDelay: '2s' }}>
              <BarChart3 className="w-6 h-6 text-lime-400" />
              <span className="font-semibold text-white">{t('landing.analytics')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
